import { useState, useEffect } from "react";
import "./FormContentRating.css";
import { Question, UpdateQuestionPayload } from "effno-core";
import { apiUpdateQuestion } from "../../services/QuestionService";
import { toastFail, toastSuccess } from "../../config";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { selectEditingForm, selectUIStates, setEditingForm } from "../../redux/features/appData";
import { useDispatch } from "react-redux";
import { RATING_SETTINGS } from "../config/questionSettings";
import { DEBOUNCE_TIME } from "../config/forms";
import { apiFormHasEdits } from "../../services/FormService";

type FormContentRatingProps = {
    question: Question;
    allowSaving?: boolean;
}

export const FormContentRating = ({question, allowSaving = true} : FormContentRatingProps) => {

    const dispatch = useDispatch();

    const editingForm = useSelector(selectEditingForm);
    const uiStates = useSelector(selectUIStates);

    const [title, setTitle] = useState<string>("");
    const [description, setDescription] = useState<string>("");

    const [debouncedData, setDebouncedData] = useState<any | undefined>(undefined);
    const [debounceSettings, setDebounceSettings] = useState<any | undefined>(undefined);


    useEffect(() => {
        setTitle(question.title);
        setDescription(question.description);
    }, [question]);

    // Debounce settings changes
    useEffect(() => {
        const handler = setTimeout(() => {
            if (!debounceSettings) {
                return;
            }
            setDebouncedData(debounceSettings);
        }, DEBOUNCE_TIME);

        // Clear the timeout if settings changes (i.e., user is still typing)
        return () => {
            clearTimeout(handler);
        };
    }, [debounceSettings]);

    // Effect to save the debounced data whenever it changes
    useEffect(() => {
        (async () => {
            if (!debouncedData) {
                return;
            }

            console.log("debouncedData: ", debouncedData);
            
            await handleSave();
        })()
    }, [debouncedData]);

    useEffect(() => {
        setDebounceSettings({
            title,
            description,
        })
    }, [title, description])

    const updateRedux = () => {
        if (!editingForm) {
            return;
        }

        const index = editingForm.questions.findIndex((q: Question) =>  q.id === question.id);

        if (index === -1) {
            toast("Can't find question id", toastFail);
            return;
        }

        const newQuestions = [...editingForm.questions];
        newQuestions[index] = {
            ...question,
            title,
            description,
        };

        dispatch(setEditingForm({...editingForm, hasEdits: true, questions: newQuestions}));
    }

    const handleSave = async () => {
        if (!editingForm || !allowSaving) {
            return;
        }

        // update question in redux first to avoid delay lag causing issues
        updateRedux();

        const settings = question.settings ? JSON.parse(question.settings) : RATING_SETTINGS;

        const payload : UpdateQuestionPayload = {
            questionId: question.id,
            type: question.type,
            title: title,
            description: description,
            required: question.required,
            settings: JSON.stringify(settings),  // these don't change in this instance
            conditions: question.conditions
        }

        const newQuestion = await apiUpdateQuestion(payload);

        if (!newQuestion) {
            toast("Error saving update", toastFail);
            return;
        }

        await apiFormHasEdits(editingForm.id);
        console.log("Question updated");

        // update redux
        // const index = editingForm.questions.findIndex((q: Question) =>  q.id === question.id);

        // if (index === -1) {
        //     toast("Can't find question id", toastFail);
        //     return;
        // }

        // const newQuestions = [...editingForm.questions];
        // newQuestions[index] = newQuestion;

        // await apiFormHasEdits(editingForm.id);

        // dispatch(setEditingForm({...editingForm, hasEdits: true, questions: newQuestions}));

        // console.log("Question updated");

    }

    const settings = JSON.parse(question.settings);

    const min = 1;
    const max = parseInt(settings.stars) || 5; 

    const scoreArray : string [] = [];
    for (let i = min; i <= max; i++) {
        scoreArray.push(i.toString());
    }

    return (
        <div className="form-content-body">
            <div className="form-content-title">
                <input 
                    type="text"
                    placeholder="..."
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    data-testid="question-title"
                />
                {/* {question.required && <span>*</span>} */}
            </div>
            <div className="form-content-description">
                <input 
                    type="text"
                    placeholder="Description (optional)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    data-testid="question-description"
                />
            </div>   

            <div 
                className="form-content-rating"
                style={!uiStates.mobileLayout ? {marginTop: "24px"} : {}}
            >
                {scoreArray.map((score: string, index: number) => {
                    return (
                        <svg 
                            key={"star-" + index}
                            className="form-content-star"
                            style={uiStates.mobileLayout ? 
                                {width: `${(5-(Math.floor(scoreArray.length / 3)))*8}px`} :
                                {width: "32px"}
                            }
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            
                        >
                            <polygon points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9" />
                        </svg>
                        
                    )
                })}
            </div>    

            {/* <button 
                onClick={handleSave}
                data-testid="save-question"
            >
                temp save button before debounce 
            </button> */}
        </div>
    )
}